import { useEffect, useMemo, useState } from 'react'
import { useUnderwritingStore } from '../../store/underwritingStore'
import useDealStore from '../../store/dealStore'
import MetricsPanel from './MetricsPanel'
import CashFlowTable from './CashFlowTable'
import AssumptionsPanel from './AssumptionsPanel'
import ScenarioBar from './ScenarioBar'
import {
  findRecommendedCounterOffer,
  getMinimumCounterOffer,
  validateCounterOffer,
} from '../../utils/renegotiationValidation'
import { formatCurrency } from '../../utils/currencyUtils'
import '../../styles/underwriting.css'

const TABS = [
  { id: 'compare', label: 'Scenario Compare', icon: '🔀' },
  { id: 'metrics', label: 'Key Metrics', icon: '📊' },
  { id: 'cashflow', label: '5-Year Cash Flow', icon: '📈' },
  { id: 'assumptions', label: 'Assumptions', icon: '⚙️' },
]

// Money format using the centralized currency formatting utility
const fmtMoneyMM = (n) => formatCurrency(n)

// ── Market intelligence used by the AI Smart-Fill ──────────────────────────
const MARKET_RATES = {
  TX: { interestRate: 0.065, exitCapRate: 0.055, vacancyAdj: -0.01, rentGrowth: 0.035 },
  FL: { interestRate: 0.066, exitCapRate: 0.056, vacancyAdj: -0.01, rentGrowth: 0.032 },
  TN: { interestRate: 0.067, exitCapRate: 0.057, vacancyAdj: 0.00, rentGrowth: 0.030 },
  NC: { interestRate: 0.066, exitCapRate: 0.056, vacancyAdj: -0.01, rentGrowth: 0.031 },
  GA: { interestRate: 0.067, exitCapRate: 0.058, vacancyAdj: 0.00, rentGrowth: 0.030 },
  AZ: { interestRate: 0.068, exitCapRate: 0.058, vacancyAdj: 0.00, rentGrowth: 0.028 },
  CO: { interestRate: 0.069, exitCapRate: 0.057, vacancyAdj: -0.01, rentGrowth: 0.029 },
  CA: { interestRate: 0.072, exitCapRate: 0.045, vacancyAdj: -0.02, rentGrowth: 0.025 },
  NY: { interestRate: 0.073, exitCapRate: 0.046, vacancyAdj: -0.02, rentGrowth: 0.022 },
}
const DEFAULT_MARKET = { interestRate: 0.068, exitCapRate: 0.058, vacancyAdj: 0, rentGrowth: 0.030 }

// ── What-If Offer Calculator ─────────────────────────────────────────────────
function WhatIfCalculator({ scenario }) {
  const { calculateWithPrice } = useUnderwritingStore()
  const basePrice = scenario.purchasePrice || 0
  const [sliderPrice, setSliderPrice] = useState(basePrice)
  const min = Math.round(basePrice * 0.75)
  const max = Math.round(basePrice * 1.10)

  const wi = useMemo(() => calculateWithPrice(sliderPrice), [sliderPrice, calculateWithPrice])

  const fmt = (n) => `$${Math.round(n || 0).toLocaleString()}`
  const pct = (n) => `${((n || 0) * 100).toFixed(2)}%`
  const pct1 = (n) => `${((n || 0) * 100).toFixed(1)}%`

  const metrics = [
    { label: 'CAP RATE', value: pct(wi.capRate), pass: wi.capRate >= 0.05, note: wi.capRate >= 0.05 ? '✓ Above 5%' : '✗ Below 5%' },
    { label: 'DSCR', value: `${(wi.dscr || 0).toFixed(2)}x`, pass: wi.dscr >= 1.25, note: wi.dscr >= 1.25 ? '✓ Lender OK' : '✗ Below 1.25' },
    { label: 'ANNUAL CASH FLOW', value: fmt(wi.cashFlowBeforeTax), pass: wi.cashFlowBeforeTax > 0, note: wi.cashFlowBeforeTax > 0 ? '✓ Positive' : '✗ Negative' },
    { label: 'CASH-ON-CASH', value: pct1(wi.cashOnCash), pass: wi.cashOnCash >= 0.06, note: wi.cashOnCash >= 0.06 ? '✓ Above 6%' : '✗ Below 6%' },
  ]

  return (
    <div className="uw-whatif-wrap" style={{
      margin: '0', padding: '24px 28px',
      background: '#f0f6ff',
      borderTop: '1px solid #dbeafe',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>🔢</span>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#1e3a8a' }}>What-If Offer Calculator</span>
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#3b82f6', letterSpacing: 0.5 }}>LIVE PREVIEW</span>
      </div>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#475569' }}>
        Drag the slider to explore how your offer price changes cap rate, DSCR, and cash flow in real time.
      </p>

      {/* Slider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{fmt(min)}</span>
        <input
          type="range"
          min={min} max={max} step={10000}
          value={sliderPrice}
          onChange={e => setSliderPrice(Number(e.target.value))}
          style={{ flex: 1, accentColor: '#1d4ed8' }}
        />
        <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{fmt(max)}</span>
      </div>
      <div style={{ textAlign: 'center', fontSize: 18, fontWeight: 800, color: '#1d4ed8', marginBottom: 16 }}>
        {fmt(sliderPrice)}
      </div>

      {/* Metric tiles */}
      <div className="uw-whatif-metrics" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
        {metrics.map(m => (
          <div key={m.label} style={{
            background: m.pass ? '#eff6ff' : '#fff',
            border: `1.5px solid ${m.pass ? '#bfdbfe' : '#e2e8f0'}`,
            borderRadius: 10, padding: '14px 16px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: 0.8, marginBottom: 6 }}>{m.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: m.pass ? '#1d4ed8' : '#94a3b8', marginBottom: 4 }}>{m.value}</div>
            <div style={{ fontSize: 11, color: m.pass ? '#3b82f6' : '#94a3b8', fontWeight: 600 }}>{m.note}</div>
          </div>
        ))}
      </div>

      {/* Total monthly cost */}
      {wi.totalMonthlyOwnershipCost > 0 && (
        <div style={{ textAlign: 'center', padding: '10px', background: '#dbeafe', borderRadius: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#1e40af', letterSpacing: 0.5, marginBottom: 2 }}>TOTAL MONTHLY COST</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#1e3a8a' }}>{fmt(wi.totalMonthlyOwnershipCost)}/mo</div>
          <div style={{ fontSize: 11, color: '#3b82f6', fontWeight: 600 }}>Debt + insurance + op. exp.</div>
        </div>
      )}
    </div>
  )
}

// ── Renegotiation Calculator — shown when underwriting score < 70 ────────────
function RenegotiationSection({ currentScore, askingPrice, scoreMetrics, suggestedPrice }) {
  const { calculateWithPrice, updatePurchasePrice } = useUnderwritingStore()
  const { updateDealState, dealState } = useDealStore()
  const [counterPrice, setCounterPrice] = useState('')
  const [appliedAt, setAppliedAt] = useState(0)  // bumped on Apply → forces preview re-calc
  const [appliedPrice, setAppliedPrice] = useState(null)
  const [showCalc, setShowCalc] = useState(true)
  const [priceWarning, setPriceWarning] = useState(null)

  // Live preview — recomputes when price typed OR after Apply (appliedAt changes)
  const activePrice = Number(counterPrice) || 0
  const preview = useMemo(() => {
    if (!activePrice || activePrice <= 0) return null
    void appliedAt  // include so memo re-runs after Apply updates the store
    return calculateWithPrice(activePrice)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePrice, calculateWithPrice, appliedAt])

  const previewPassCount = preview ? scoreMetrics.filter(m => m.pass(preview)).length : 0
  const previewScore = Math.round((previewPassCount / scoreMetrics.length) * 100)
  const previewPasses = previewScore >= 70
  const minimumReasonableOffer = useMemo(
    () => getMinimumCounterOffer(askingPrice, suggestedPrice),
    [askingPrice, suggestedPrice],
  )

  const handleApply = () => {
    const validation = validateCounterOffer({
      counterPrice: activePrice,
      askingPrice,
      recommendedPrice: suggestedPrice,
    })
    if (!validation.valid) {
      if (validation.reason === 'missing') return
      setPriceWarning({
        entered: validation.entered,
        minimum: validation.minimum,
        suggested: suggestedPrice,
      })
      return
    }
    if (!previewPasses) {
      setPriceWarning({
        entered: activePrice,
        minimum: minimumReasonableOffer,
        suggested: suggestedPrice,
        doesNotPass: true,
      })
      return
    }
    updatePurchasePrice(activePrice)
    const completedAt = new Date().toISOString()
    updateDealState({
      rawDealData: {
        ...dealState.rawDealData,
        originalAskingPrice: askingPrice,
        price: askingPrice,
      },
      renegotiation: {
        renegotiatedPrice: activePrice,
        originalPrice: askingPrice,
        minimumAllowedPrice: minimumReasonableOffer,
        isValid: true,
        completed: true,
        status: 'completed',
        achievedScore: previewScore,
        priceReduction: askingPrice - activePrice,
        renegotiatedAt: completedAt,
        completedAt,
        source: 'underwriting-calculator',
      },
    })
    setAppliedPrice(activePrice)
    setAppliedAt(Date.now())  // force preview useMemo to re-run with fresh store data
  }

  const isApplied = appliedPrice === activePrice && appliedPrice !== null

  return (
    <div style={{ position: 'relative' }}>
      {priceWarning && (
        <div className="uw-renego-modal-backdrop" role="presentation">
          <div className="uw-renego-modal" role="alertdialog" aria-modal="true" aria-labelledby="uw-renego-warning-title">
            <div className="uw-renego-modal-icon">!</div>
            <div>
              <h3 id="uw-renego-warning-title">Counter offer is too low</h3>
              <p>
                {priceWarning.doesNotPass
                  ? `$${priceWarning.entered.toLocaleString()} does not improve the underwriting score enough to proceed.`
                  : `$${priceWarning.entered.toLocaleString()} is not a realistic counter offer for an asking price of $${askingPrice.toLocaleString()}.`}
              </p>
              <p>
                Enter at least ${Math.round(priceWarning.minimum).toLocaleString()}
                {priceWarning.suggested ? ', which is the recommended minimum offer.' : '.'}
              </p>
              <button
                type="button"
                onClick={() => {
                  setPriceWarning(null)
                  setAppliedPrice(null)
                  setShowCalc(true)
                }}
              >
                Continue renegotiation
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{
        margin: '0 0 16px',
        border: '2px solid #f59e0b',
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 4px 16px rgba(245,158,11,0.15)',
      }}>
      {/* Header — click to collapse/expand */}
      <div
        onClick={() => setShowCalc(v => !v)}
        style={{
          background: 'linear-gradient(135deg, #92400e, #b45309)',
          padding: '14px 20px',
          display: 'flex', alignItems: 'center', gap: 12,
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 22 }}>🔄</span>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: 15 }}>
            Renegotiation Calculator
          </div>
          <div style={{ color: '#fde68a', fontSize: 12, marginTop: 2 }}>
            Score {currentScore}/100 — need ≥ 70 to advance. Enter a lower counter offer to unlock.
          </div>
        </div>
        <span style={{ color: '#fde68a', fontSize: 18 }}>{showCalc ? '▲' : '▼'}</span>
      </div>

      {showCalc && (
        <div style={{
          background: 'linear-gradient(135deg, #fefce8, #fff7ed)',
          padding: 20,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 20,
        }}>
          {/* ── LEFT: Input ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontWeight: 800, color: '#92400e', fontSize: 13 }}>
              STEP 1 — Enter Counter Offer Price
            </div>

            {/* AI-suggested target price */}
            {suggestedPrice ? (
              <div style={{
                background: '#dcfce7', border: '1px solid #86efac',
                borderRadius: 8, padding: '10px 14px', fontSize: 12,
              }}>
                <span style={{ fontWeight: 700, color: '#15803d' }}>💡 AI Suggestion: </span>
                <span style={{ color: '#166534' }}>
                  Offer <strong>${suggestedPrice.toLocaleString()}</strong> to reach score 70
                  &nbsp;(saves <strong>${(askingPrice - suggestedPrice).toLocaleString()}</strong> off asking).
                </span>
                <button
                  onClick={() => { setCounterPrice(String(suggestedPrice)); setAppliedPrice(null) }}
                  style={{
                    display: 'block', marginTop: 6, padding: '4px 10px',
                    background: '#15803d', color: '#fff', border: 'none',
                    borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  Use ${suggestedPrice.toLocaleString()}
                </button>
              </div>
            ) : (
              <div style={{
                background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#991b1b',
              }}>
                ⚠️ Price reduction alone may not reach 70. Also try adjusting expenses
                or vacancy in the Assumptions tab above.
              </div>
            )}

            <div>
              <label style={{
                fontSize: 12, fontWeight: 700, color: '#78350f',
                display: 'flex', justifyContent: 'space-between', marginBottom: 6,
              }}>
                <span>Counter Offer Price ($) <span style={{ color: '#dc2626' }}>*</span></span>
                {askingPrice > 0 && (
                  <span style={{ color: '#d97706', fontWeight: 400 }}>
                    Asking: ${askingPrice.toLocaleString()}
                  </span>
                )}
              </label>
              <input
                type="number"
                value={counterPrice}
                onChange={e => { setCounterPrice(e.target.value); setAppliedPrice(null) }}
                placeholder={suggestedPrice ? `e.g. ${suggestedPrice.toLocaleString()}` : 'Enter counter offer price'}
                style={{
                  width: '100%', padding: '11px 14px', boxSizing: 'border-box',
                  border: `2px solid ${previewPasses && preview ? '#22c55e' : '#f59e0b'}`,
                  borderRadius: 8, fontSize: 15, outline: 'none',
                  background: previewPasses && preview ? '#f0fdf4' : '#fffbeb',
                  fontWeight: 600, color: '#0f172a',
                  transition: 'border-color 0.2s, background 0.2s',
                }}
              />
              {askingPrice > 0 && activePrice > 0 && (
                <div style={{ fontSize: 11, color: '#d97706', marginTop: 4 }}>
                  {activePrice < askingPrice
                    ? `▼ ${((1 - activePrice / askingPrice) * 100).toFixed(1)}% below asking — saves $${(askingPrice - activePrice).toLocaleString()}`
                    : activePrice === askingPrice
                      ? '= Same as asking price — enter a lower price to improve score'
                      : `▲ ${((activePrice / askingPrice - 1) * 100).toFixed(1)}% above asking`}
                </div>
              )}
            </div>

            <button
              disabled={!activePrice || activePrice <= 0}
              onClick={handleApply}
              style={{
                padding: '12px 20px', borderRadius: 8, border: 'none',
                background: isApplied
                  ? 'linear-gradient(135deg, #16a34a, #15803d)'
                  : activePrice > 0
                    ? 'linear-gradient(135deg, #1d4ed8, #1e40af)'
                    : '#e2e8f0',
                color: activePrice > 0 ? '#fff' : '#94a3b8',
                fontWeight: 800, fontSize: 14,
                cursor: activePrice > 0 ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
              }}
            >
              {isApplied
                ? `✓ Applied $${activePrice.toLocaleString()} — check score below`
                : activePrice > 0
                  ? `🔁 Apply $${activePrice.toLocaleString()} & Recalculate`
                  : 'Enter a counter price above'}
            </button>

            {isApplied && previewPasses && (
              <div style={{
                background: '#dcfce7', border: '1px solid #86efac',
                borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#166534', fontWeight: 600,
              }}>
                Score now {previewScore}/100 — the <strong>"Complete Underwriting &amp; Advance"</strong> button below is now enabled!
              </div>
            )}
            {isApplied && !previewPasses && (
              <div style={{
                background: '#fef9f0', border: '1px solid #fde68a',
                borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#92400e',
              }}>
                Score is {previewScore}/100 — still {70 - previewScore} pts short of 70.
                Try a lower price{suggestedPrice ? ` (suggested: $${suggestedPrice.toLocaleString()})` : ' or adjust assumptions'}.
              </div>
            )}
          </div>

          {/* ── RIGHT: Live score preview ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontWeight: 800, color: '#92400e', fontSize: 13 }}>
              STEP 2 — Live Score Preview
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{
                background: '#fff', border: '2px solid #fecaca',
                borderRadius: 10, padding: 14, textAlign: 'center',
              }}>
                <div style={{ fontSize: 10, color: '#ef4444', fontWeight: 700, marginBottom: 4, letterSpacing: 0.5 }}>CURRENT</div>
                <div style={{ fontSize: 32, fontWeight: 900, color: '#dc2626', lineHeight: 1 }}>{currentScore}</div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>/100</div>
                <div style={{ fontSize: 10, marginTop: 5, color: '#ef4444', fontWeight: 600 }}>Below 70</div>
              </div>
              <div style={{
                background: '#fff',
                border: `2px solid ${preview ? (previewPasses ? '#22c55e' : '#fde68a') : '#e2e8f0'}`,
                borderRadius: 10, padding: 14, textAlign: 'center',
                transition: 'border-color 0.3s',
              }}>
                <div style={{
                  fontSize: 10, fontWeight: 700, marginBottom: 4, letterSpacing: 0.5,
                  color: preview ? (previewPasses ? '#16a34a' : '#d97706') : '#94a3b8',
                }}>
                  PROJECTED
                </div>
                <div style={{
                  fontSize: 32, fontWeight: 900, lineHeight: 1,
                  color: preview ? (previewPasses ? '#16a34a' : '#d97706') : '#94a3b8',
                  transition: 'color 0.3s',
                }}>
                  {preview ? previewScore : '—'}
                </div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>/100</div>
                <div style={{
                  fontSize: 10, marginTop: 5, fontWeight: 600,
                  color: preview ? (previewPasses ? '#16a34a' : '#d97706') : '#94a3b8',
                }}>
                  {preview
                    ? previewPasses ? '✓ Passes 70!' : `Need ${70 - previewScore} more pts`
                    : 'Enter price above'}
                </div>
              </div>
            </div>

            {/* Metric tiles — update live */}
            {preview && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  { label: 'Cap Rate', value: `${(preview.capRate * 100).toFixed(2)}%`, good: preview.capRate >= 0.05, note: preview.capRate >= 0.05 ? '✓ ≥ 5%' : '✗ < 5%' },
                  { label: 'DSCR', value: `${preview.dscr.toFixed(2)}x`, good: preview.dscr >= 1.25, note: preview.dscr >= 1.25 ? '✓ ≥ 1.25x' : '✗ < 1.25x' },
                  { label: 'Cash-on-Cash', value: `${(preview.cashOnCash * 100).toFixed(1)}%`, good: preview.cashOnCash >= 0.06, note: preview.cashOnCash >= 0.06 ? '✓ ≥ 6%' : '✗ < 6%' },
                  { label: 'IRR (5-yr)', value: `${(preview.irr * 100).toFixed(1)}%`, good: preview.irr >= 0.12, note: preview.irr >= 0.12 ? '✓ ≥ 12%' : '✗ < 12%' },
                ].map(m => (
                  <div key={m.label} style={{
                    background: m.good ? '#f0fdf4' : '#fef9f0',
                    border: `1px solid ${m.good ? '#bbf7d0' : '#fde68a'}`,
                    borderRadius: 8, padding: '8px 10px', transition: 'all 0.2s',
                  }}>
                    <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2, fontWeight: 600 }}>{m.label}</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: m.good ? '#15803d' : '#d97706' }}>{m.value}</div>
                    <div style={{ fontSize: 10, color: m.good ? '#16a34a' : '#d97706', marginTop: 1 }}>{m.note}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  )
}

export default function UnderwritingEngine({ dealState, onComplete }) {
  const {
    activeTab,
    setActiveTab,
    activeScenario,
    scenarios,
    isCompleted,
    markCompleted,
    setScenariosFromDealState,
    getResults,
    getAllResults,
  } = useUnderwritingStore()

  const [smartFillDone, setSmartFillDone] = useState(false)

  // ── Market benchmarks (FRED live-rate integration removed) ─────────────────
  const stateCode = dealState?.rawDealData?.location?.state || 'TX'

  // Re-initialise when the deal ID changes OR when Module 3 submits new financials
  // (financialsUpdatedAt is bumped on every Module 3 submit so the engine always
  // uses the latest extracted NOI, expenses, and purchase price).
  useEffect(() => {
    if (dealState?.dealId) {
      setScenariosFromDealState(dealState)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dealState?.dealId,
    dealState?.financialsUpdatedAt,
    dealState?.renegotiation?.completedAt,
  ])

  // ── AI Smart-Fill — uses the static MARKET_RATES table ─────────────────────
  const handleSmartFill = () => {
    const market = MARKET_RATES[stateCode] || DEFAULT_MARKET
    const base = { ...scenarios.base }

    base.interestRate = market.interestRate
    base.exitCapRate = market.exitCapRate
    base.annualRentGrowth = market.rentGrowth
    base.vacancyRate = Math.max(0.03, Math.min(0.15, base.vacancyRate + market.vacancyAdj))
    base.loanAmount = Math.round(base.purchasePrice * 0.70)

    setScenariosFromDealState({
      ...dealState,
      _smartFillOverride: base,
    })
    setSmartFillDone(true)
    setTimeout(() => setSmartFillDone(false), 3000)
  }

  const results = getResults()
  const scenario = scenarios[activeScenario]
  const storedOriginalAsking = Number(dealState?.renegotiation?.originalPrice || dealState?.rawDealData?.originalAskingPrice)
  const rawAskingPrice = Number(dealState?.rawDealData?.price)
  const originalAskingPrice = storedOriginalAsking > 0
    ? storedOriginalAsking
    : rawAskingPrice >= 1_000
      ? rawAskingPrice
      : scenario.purchasePrice
  const hasRenegotiatedPrice = Number(dealState?.renegotiation?.renegotiatedPrice) > 0
  const renegotiationCompleted = dealState?.renegotiation?.completed === true
  const deal = {
    name: dealState?.dealName || 'Active Deal',
    address: dealState?.rawDealData?.location?.address || 'Address pending',
    units: dealState?.rawDealData?.property?.units || 0,
  }

  // ── MANDATORY GATE: base case score must be ≥ 70 to advance ──────────────
  const allResults = getAllResults()
  const basePassCount = SCORE_METRICS.filter(m => m.pass(allResults.base)).length
  const baseScore = Math.round((basePassCount / SCORE_METRICS.length) * 100)
  const suggestedPrice = useMemo(() => findRecommendedCounterOffer({
    askingPrice: originalAskingPrice,
    scoreAtPrice: (price) => {
      const result = useUnderwritingStore.getState().calculateWithPrice(price)
      const passCount = SCORE_METRICS.filter(metric => metric.pass(result)).length
      return Math.round((passCount / SCORE_METRICS.length) * 100)
    },
  }), [originalAskingPrice, scenarios])
  const persistedCounterPrice = Number(dealState?.renegotiation?.renegotiatedPrice)
  const persistedCounterValidation = persistedCounterPrice > 0
    ? validateCounterOffer({
      counterPrice: persistedCounterPrice,
      askingPrice: originalAskingPrice,
      recommendedPrice: suggestedPrice,
    })
    : { valid: true }
  const canAdvance = baseScore >= 70
    && persistedCounterValidation.valid
    && (!hasRenegotiatedPrice || renegotiationCompleted)

  const handleComplete = () => {
    if (!canAdvance) return
    markCompleted()
    if (onComplete) onComplete(results, scenario)
  }

  const dscrOk = results.dscr >= 1.25
  const capOk = results.capRate >= 0.05
  const cashOk = results.cashOnCash >= 0.06
  const irrOk = results.irr >= 0.12

  const passCount = [dscrOk, capOk, cashOk, irrOk].filter(Boolean).length


  return (
    <div className="uw-container">
      <div className="uw-header">
        <div className="uw-header-left">
          <div className="uw-module-badge">MODULE 3</div>
          <div>
            <h1 className="uw-title">Underwriting Engine</h1>
            <p className="uw-subtitle">
              {deal.name} - {deal.units || 'TBD'} Units - {deal.address}
            </p>
          </div>
        </div>
      </div>

      <ScenarioBar />

      {/* ── KPI grid: 4×2 metric blocks ── */}
      <div className="uw-kpi-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gridTemplateRows: 'auto auto',
        gap: '0.75rem',
        marginBottom: '1.5rem',
      }}>

        {/* ── Row 1: NOI · Cap Rate · DSCR · Cash-on-Cash ── */}
        <KPI label="NOI" value={fmtMoneyMM(results.noi)} sub="Net Operating Income" ok={results.noi > 0} />
        <KPI label="Cap Rate" value={`${(results.capRate * 100).toFixed(2)}%`} sub="Purchase Price" ok={capOk} />
        <KPI label="DSCR" value={results.dscr.toFixed(2)} sub="Min 1.25x required" ok={dscrOk} />
        <KPI label="Cash-on-Cash" value={`${(results.cashOnCash * 100).toFixed(1)}%`} sub="Equity deployed" ok={cashOk} />

        {/* ── Row 2: IRR · Annual CF · Break-Even · Insurance ── */}
        <KPI label="IRR (5-yr)" value={`${(results.irr * 100).toFixed(1)}%`} sub="Internal rate of return" ok={irrOk} />
        <KPI label="Annual CF" value={fmtMoneyMM(results.cashFlowBeforeTax)} sub="Before tax" ok={results.cashFlowBeforeTax > 0} />
        <KPI
          label="Break-Even Occ."
          value={results.breakEvenOccupancy != null ? `${(results.breakEvenOccupancy * 100).toFixed(1)}%` : '—'}
          sub="Required occupancy"
          ok={results.breakEvenOccupancy != null && results.breakEvenOccupancy < 0.85}
        />
        <KPI
          label={`Insurance${results.insuranceIsEstimated ? ' (est.)' : ''}`}
          value={results.monthlyInsurance != null ? `$${results.monthlyInsurance.toLocaleString()}/mo` : '—'}
          sub="Annual property insurance"
          ok={true}
        />
      </div>

      <div className="uw-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`uw-tab ${activeTab === tab.id ? 'uw-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span>{tab.icon}</span> {tab.label}
          </button>
        ))}
      </div>

      <div className="uw-content">
        {activeTab === 'metrics' && <MetricsPanel results={results} passCount={passCount} />}
        {activeTab === 'cashflow' && <CashFlowTable />}
        {activeTab === 'assumptions' && (
          <>
            {/* AI Smart-Fill banner — driven by the static market table */}
            {(() => {
              const market = MARKET_RATES[stateCode] || DEFAULT_MARKET
              const fillRate = market.interestRate
              const fillExit = market.exitCapRate
              const fillVac = Math.max(0.03, Math.min(0.15, (scenario?.vacancyRate || 0.07) + market.vacancyAdj))
              const fillRent = market.rentGrowth

              return (
                <div className="uw-smart-fill-banner">
                  <div className="uw-smart-fill-left">
                    <span className="uw-smart-fill-icon">🤖</span>
                    <div>
                      <div className="uw-smart-fill-title">
                        {`AI Smart Fill · ${stateCode} Market Benchmarks`}
                      </div>
                      <div className="uw-smart-fill-desc">
                        Will apply:&nbsp;
                        <strong>{(fillRate * 100).toFixed(2)}% interest rate</strong>
                        {' · '}
                        <strong>{(fillExit * 100).toFixed(1)}% exit cap</strong>
                        {' · '}
                        <strong>{(fillVac * 100).toFixed(0)}% vacancy</strong>
                        {' · '}
                        <strong>{(fillRent * 100).toFixed(1)}% rent growth</strong>
                        {' — based on '}
                        <strong>{stateCode}</strong> market benchmarks
                      </div>
                    </div>
                  </div>
                  <button
                    className={`uw-smart-fill-btn ${smartFillDone ? 'uw-smart-fill-btn--done' : ''}`}
                    onClick={handleSmartFill}
                  >
                    {smartFillDone ? '✓ Applied!' : '⚡ Smart Fill'}
                  </button>
                </div>
              )
            })()}
            <AssumptionsPanel />
          </>
        )}
        {/* Feature 1 — Scenario Compare tab */}
        {activeTab === 'compare' && <ScenarioComparePanel getAllResults={getAllResults} scenarios={scenarios} />}
      </div>

      {/* ── What-If Offer Calculator ── */}
      <WhatIfCalculator scenario={scenario} />

      <div className="uw-footer">
        <span>Asking Price: <strong>${originalAskingPrice.toLocaleString()}</strong></span>
        {hasRenegotiatedPrice && (
          <span>Counter Offer: <strong>${scenario.purchasePrice.toLocaleString()}</strong></span>
        )}
        <span>Loan: <strong>${Math.round(scenario.loanAmount).toLocaleString()} @ {(scenario.interestRate * 100).toFixed(2)}% / {scenario.amortization}yr</strong></span>
        <span>Equity: <strong>{fmtMoneyMM(results.equity)}</strong></span>
        <span>Hold Period: <strong>{scenario.holdYears} Years</strong></span>
      </div>
      {renegotiationCompleted && persistedCounterValidation.valid && (
        <div className="uw-renego-complete">
          <strong>Renegotiation saved</strong>
          <span>
            Counteroffer ${persistedCounterPrice.toLocaleString()} accepted for underwriting
            {Number(dealState?.renegotiation?.achievedScore) > 0
              ? ` at score ${dealState.renegotiation.achievedScore}/100.`
              : '.'}
          </span>
        </div>
      )}
      {/* Once completed, the renegotiation calculator stays closed permanently. */}
      {!canAdvance && !renegotiationCompleted && (
        <div style={{ padding: '0 0 0' }}>
          <RenegotiationSection
            currentScore={baseScore}
            askingPrice={originalAskingPrice}
            scoreMetrics={SCORE_METRICS}
            suggestedPrice={suggestedPrice}
          />
        </div>
      )}

      <div className="module-bottom-advance">
        {!canAdvance && (
          <div className="uw-gate-block" style={{
            background: '#fef2f2',
            border: '1.5px solid #dc2626',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '12px',
            color: '#991b1b',
            fontWeight: 600,
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <span style={{ fontSize: '18px' }}>🚫</span>
            <span>
              {!persistedCounterValidation.valid
                ? <>The saved counteroffer is below the minimum acceptable amount. Continue renegotiation with an offer of at least <strong>${Math.round(persistedCounterValidation.minimum).toLocaleString()}</strong>.</>
                : <>Base case score is <strong>{baseScore}/100</strong> — a minimum score of <strong>70</strong> is required to advance to the next module. Improve the deal assumptions until the base case reaches 70 or higher.</>}
            </span>
          </div>
        )}
        <button
          className={`uw-btn-complete ${isCompleted ? 'uw-btn-complete--done' : ''} ${!canAdvance ? 'uw-btn-complete--blocked' : ''}`}
          onClick={handleComplete}
          disabled={isCompleted || !canAdvance}
          style={!canAdvance ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
        >
          {isCompleted ? 'Underwriting Complete' : 'Complete Underwriting & Advance to Module 4'}
        </button>
      </div>
    </div>
  )
}

function KPI({ label, value, sub, ok }) {
  return (
    <div className={`uw-kpi ${ok ? 'uw-kpi--pass' : 'uw-kpi--fail'}`}>
      <div className="uw-kpi-indicator">{ok ? 'Y' : 'N'}</div>
      <div className="uw-kpi-value">{value}</div>
      <div className="uw-kpi-label">{label}</div>
      <div className="uw-kpi-sub">{sub}</div>
    </div>
  )
}

// ── Scoring metrics — shared by UnderwritingEngine gate and ScenarioComparePanel ──
const SCORE_METRICS = [
  {
    label: 'NOI',
    fmt: r => fmtMoneyMM(r.noi),
    pass: r => r.noi > 0,
    threshold: 'Positive NOI required',
  },
  {
    label: 'Cap Rate',
    fmt: r => `${(r.capRate * 100).toFixed(2)}%`,
    pass: r => r.capRate >= 0.05,
    threshold: '≥ 5.00% target',
  },
  {
    label: 'DSCR',
    fmt: r => r.dscr.toFixed(2) + 'x',
    pass: r => r.dscr >= 1.25,
    threshold: '≥ 1.25x lender min',
  },
  {
    label: 'Cash-on-Cash',
    fmt: r => `${(r.cashOnCash * 100).toFixed(1)}%`,
    pass: r => r.cashOnCash >= 0.06,
    threshold: '≥ 6.0% target',
  },
  {
    label: 'IRR (5-yr)',
    fmt: r => `${(r.irr * 100).toFixed(1)}%`,
    pass: r => r.irr >= 0.12,
    threshold: '≥ 12% target',
  },
  {
    label: 'Annual Cash Flow',
    fmt: r => fmtMoneyMM(r.cashFlowBeforeTax),
    pass: r => r.cashFlowBeforeTax > 0,
    threshold: 'Positive CF required',
  },
  {
    label: 'Break-Even Occupancy',
    fmt: r => r.breakEvenOccupancy != null ? `${(r.breakEvenOccupancy * 100).toFixed(1)}%` : '—',
    pass: r => r.breakEvenOccupancy != null && r.breakEvenOccupancy < 0.85,
    threshold: '< 85% target',
  },
  {
    label: 'Insurance',
    fmt: r => r.monthlyInsurance != null ? `$${r.monthlyInsurance.toLocaleString()}/mo` : '—',
    pass: () => true,
    threshold: 'Included in underwriting',
  },
]

// ── Feature 1: Scenario Compare Panel ──────────────────────────────────────
function ScenarioComparePanel({ getAllResults, scenarios }) {
  const all = getAllResults()
  const [openInfoKey, setOpenInfoKey] = useState(null)

  const METRICS = SCORE_METRICS

  const cols = [
    { key: 'best', label: 'Best Case' },
    { key: 'base', label: 'Base Case' },
    { key: 'worst', label: 'Worst Case' },
  ]

  // Summary: how many metrics pass per scenario
  const passCounts = {}
  cols.forEach(c => {
    passCounts[c.key] = METRICS.filter(m => m.pass(all[c.key])).length
  })

  const scorePct = (n) => Math.round((n / METRICS.length) * 100)
  const scoreTone = (score) => {
    if (score < 70) return { color: '#dc2626', label: 'Red', verdict: 'Review' }
    if (score < 75) return { color: '#f59e0b', label: 'Orange', verdict: 'Borderline' }
    return { color: '#16a34a', label: 'Green', verdict: 'Proceed' }
  }

  return (
    <div className="uw-compare">
      <h3 className="uw-section-title">Scenario Side-by-Side</h3>
      <p className="uw-cf-desc">
        All three scenarios run through the same financial model. Score color is red below 70, orange from 70 to 74, and green at 75 or higher.
      </p>

      {/* Summary verdict row */}
      <div className="uw-cmp-summary-row">
        {cols.map(col => {
          const score = scorePct(passCounts[col.key])
          const tone = scoreTone(score)
          const isInfoOpen = openInfoKey === col.key
          return (
            <div key={col.key} className="uw-cmp-summary-card">
              <button
                type="button"
                className="uw-cmp-info-btn"
                aria-label={`Explain ${col.label} score`}
                aria-expanded={isInfoOpen}
                onClick={() => setOpenInfoKey(value => value === col.key ? null : col.key)}
              >
                i
              </button>
              <div className="uw-cmp-summary-label">{col.label}</div>
              <div className="uw-cmp-summary-score" style={{ color: tone.color }}>
                {score}
                <span>/100</span>
              </div>
              <ScoreSemiGauge score={score} color={tone.color} label={col.label} />
              <div className="uw-cmp-verdict" style={{ color: tone.color, borderColor: tone.color }}>
                {tone.verdict} · {passCounts[col.key]}/{METRICS.length} pass
              </div>
              {isInfoOpen && (
                <div className="uw-cmp-info-popover" role="note">
                  <strong>{col.label} score: {score}/100</strong>
                  <p>
                    The score uses the same eight checks shown in the KPI cards.
                    Each passing check contributes 12.5 points.
                  </p>
                  <div className="uw-cmp-info-list">
                    {METRICS.map(metric => {
                      const scenarioResult = all[col.key]
                      const passes = metric.pass(scenarioResult)
                      return (
                        <div key={metric.label}>
                          <span>{metric.label} <small>{metric.threshold}</small></span>
                          <b className={passes ? 'is-pass' : 'is-fail'}>
                            {metric.fmt(scenarioResult)} · {passes ? 'Y' : 'N'}
                          </b>
                        </div>
                      )
                    })}
                  </div>
                  <p className="uw-cmp-info-foot">
                    Break-Even Occupancy and Insurance now match the KPI grid.
                    EGI and Total Expenses are no longer separate score checks.
                  </p>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Metric rows */}
      <div className="uw-cmp-table">
        <div className="uw-cmp-table-header">
          <span>Metric</span>
          <span>Threshold</span>
          {cols.map(c => <span key={c.key}>{c.label}</span>)}
        </div>
        {METRICS.map(m => (
          <div key={m.label} className="uw-cmp-row">
            <span className="uw-cmp-metric-label">{m.label}</span>
            <span className="uw-cmp-threshold">{m.threshold}</span>
            {cols.map(col => {
              const ok = m.pass(all[col.key])
              return (
                <span key={col.key} className={`uw-cmp-cell ${ok ? 'uw-cmp-pass' : 'uw-cmp-fail'}`}>
                  {m.fmt(all[col.key])}
                </span>
              )
            })}
          </div>
        ))}
      </div>

      <div className="uw-cf-note">
        Switch scenarios using the bar above the KPI strip to update all panels. The compare view always shows all three simultaneously.
      </div>
    </div>
  )
}

function ScoreSemiGauge({ score, color, label }) {
  const normalizedScore = Math.max(0, Math.min(100, Number(score) || 0))

  return (
    <div className="uw-cmp-semi-gauge" aria-label={`${label} score ${normalizedScore} out of 100`}>
      <svg viewBox="0 0 180 96" role="img" aria-hidden="true">
        <path
          className="uw-cmp-semi-gauge-track"
          d="M20 86 A70 70 0 0 1 160 86"
          pathLength="100"
        />
        <path
          className="uw-cmp-semi-gauge-fill"
          d="M20 86 A70 70 0 0 1 160 86"
          pathLength="100"
          style={{
            stroke: color,
            strokeDasharray: `${normalizedScore} 100`,
          }}
        />
      </svg>
      <div className="uw-cmp-semi-gauge-scale">
        <span>0</span>
        <span>100</span>
      </div>
    </div>
  )
}
